/**
 * The Rules over a period (§5.27), the organization's Rules page
 * (`OrgRulesPage`) — an organization names no rules repository of its own,
 * each workspace does: runs audited, audits asked for, what their last audits answered, the
 * runs held by a broken enforced MUST, and the rules found broken most — every
 * member's for an owner or an administrator, one's own for a member. Read off
 * the trail of runs (`GET /api/orgs/:id/rules/stats`), so an organization that
 * keeps no trail is told where the figures come from instead.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { Note, PageBody } from '../../../components/list';
import { RULES_STATS_DAYS, type RulesStats } from '@airlock/contracts/runs/rules-stats';
import { api, apiPath } from '../../../lib/api';
import { color, font, radius } from '../../../theme/tokens';
import { failureText } from '../shared';

/** The organization's Rules page: its figures, and where its rules are. */
export function OrgRulesPage({ orgId }: { orgId: string }): React.JSX.Element {
  return (
    <PageBody gap={14}>
      <Note>Rules belong to each workspace: name its rules repository on the workspace’s Projects page, and read them on its Rules page.</Note>
      <RulesStatsBand orgId={orgId} />
    </PageBody>
  );
}

export function RulesStatsBand({ orgId }: { orgId: string }): React.JSX.Element | null {
  const [days, setDays] = useState<number>(30);
  const [stats, setStats] = useState<RulesStats | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    setFailure(null);
    api
      .get<RulesStats>(apiPath`/orgs/${orgId}/rules/stats?days=${days}`)
      .then((next) => !gone && setStats(next))
      .catch((error: unknown) => !gone && setFailure(failureText(error)));
    return () => {
      gone = true;
    };
  }, [orgId, days]);

  // The band of the redesign (§29, §45.4): tiles 8 apart on the page's tile
  // grey, no kicker per tile — the figure, then the product's phrase.
  const tile: React.CSSProperties = {
    background: color.surfaceAlt,
    borderRadius: radius.xxl,
    padding: '16px 18px',
    fontVariantNumeric: 'tabular-nums'
  };
  const figure = (value: number, label: string, tone?: string): React.JSX.Element => (
    <div key={label} style={{ ...tile, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.02em', color: tone ?? color.ink }}>{value}</span>
      <span style={{ fontSize: 12.5, color: tone ?? color.inkFaint }}>{label}</span>
    </div>
  );

  return (
    <section aria-label="Rules over the period" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>
          {stats?.everyone === false ? 'YOUR RUNS' : 'EVERY MEMBER’S RUNS'}
        </span>
        <span style={{ flex: 1 }} />
        <div
          role="radiogroup"
          aria-label="Period"
          style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2, border: `1px solid ${color.line}`, borderRadius: 8, background: color.surfaceAlt }}
        >
          {RULES_STATS_DAYS.map((one) => (
            <button
              key={one}
              type="button"
              role="radio"
              aria-checked={one === days}
              onClick={() => setDays(one)}
              style={{
                height: 26,
                padding: '0 12px',
                border: 0,
                borderRadius: 6,
                background: one === days ? color.surface : 'transparent',
                boxShadow: one === days ? '0 1px 2px rgba(22,25,31,.06)' : 'none',
                color: one === days ? color.ink : color.inkSubtle,
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: one === days ? 600 : 400,
                cursor: 'pointer',
                transition: 'background .15s, color .15s'
              }}
            >
              {one} days
            </button>
          ))}
        </div>
      </div>
      {failure ? (
        <p style={{ margin: 0, fontSize: 13, color: color.badText }}>{failure}</p>
      ) : !stats ? (
        <p style={{ margin: 0, fontSize: 13, color: color.inkFaint }}>Reading the trail of runs…</p>
      ) : !stats.runs ? (
        <p style={{ ...tile, margin: 0, fontSize: 13.5, lineHeight: 1.55, color: color.inkSubtle }}>
          No run recorded in the last {stats.days} days. These figures come from the trail of runs, which the organization switches on in its settings.
        </p>
      ) : (
        <>
          <div className="al-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
            {figure(stats.audited, `of ${stats.runs} runs audited`)}
            {figure(stats.audits, 'audits asked for')}
            {figure(stats.verdicts.clean, 'clean')}
            {figure(stats.verdicts.should + stats.verdicts.must, 'with findings')}
            {figure(stats.held, 'held by a broken MUST', stats.held ? color.badText : undefined)}
            {stats.verdicts.failed ? figure(stats.verdicts.failed, 'audits that failed') : null}
          </div>
          {stats.broken.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, color: color.inkMuted }}>Broken most</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {stats.broken.map((one) => (
                  <span
                    key={one.id}
                    title={`Broken in ${one.runs} run${one.runs === 1 ? '' : 's'}${one.blocking ? `, holding the merge of ${one.blocking}` : ''}`}
                    style={{
                      height: 24,
                      padding: '0 8px',
                      border: `1px solid ${one.blocking ? color.badLine : color.line}`,
                      borderRadius: 6,
                      background: one.blocking ? color.badBg : color.surface,
                      fontFamily: font.mono,
                      fontSize: 12,
                      color: one.blocking ? color.badText : color.inkMuted,
                      overflowWrap: 'anywhere',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    {one.id} · {one.runs}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
